import type { ReactNode } from 'react'

interface PageContainerProps {
  children: ReactNode
  className?: string
}

export function PageContainer({ children, className = '' }: PageContainerProps) {
  return (
    // App.tsx의 #main-content가 유일한 <main> 랜드마크 — 중첩 main 방지를 위해 div 사용.
    // 하단 클리어런스(BottomNav+safe-area)는 #main-content의 pb가 담당하므로 여기선 콘텐츠 여백만.
    <div className={`workspace-page flex-1 px-4 py-5 sm:px-5 lg:px-8 lg:py-7 ${className}`}>
      <div className="mx-auto w-full">{children}</div>
    </div>
  )
}

interface PageHeaderProps {
  title: string
  description?: string
  action?: ReactNode
}

export function PageHeader({ title, description, action }: PageHeaderProps) {
  return (
    <div className="mb-7 lg:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="mb-2 block text-meta font-semibold uppercase tracking-[0.18em] text-accent-primary">
            Moonwave 워크스페이스
          </span>
          <h1 className="text-[clamp(1.75rem,2.2vw,2.25rem)] leading-tight font-bold tracking-[-0.035em] text-heading">
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-sm sm:text-base text-caption">
              {description}
            </p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    </div>
  )
}

interface SectionProps {
  children: ReactNode
  title?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}

export function Section({ children, title, icon, action, className = '' }: SectionProps) {
  return (
    <section className={`mb-8 ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-caption">{icon}</span>
            )}
            {title && (
              <h2 className="text-lg font-semibold text-heading">
                {title}
              </h2>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

interface GridProps {
  children: ReactNode
  cols?: 1 | 2 | 3 | 4
  gap?: 'sm' | 'md' | 'lg'
  className?: string
}

const colStyles = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
}

const gapStyles = {
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
}

export function Grid({ children, cols = 3, gap = 'md', className = '' }: GridProps) {
  return (
    <div className={`grid ${colStyles[cols]} ${gapStyles[gap]} ${className}`}>
      {children}
    </div>
  )
}

interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {icon && (
        <div className="mb-4 p-4 rounded-full bg-surface-tertiary text-caption">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-medium text-heading">
        {title}
      </h3>
      {description && (
        <p className="mt-1 text-sm text-caption max-w-sm">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function LoadingState({ message = '불러오는 중...' }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      <p className="mt-4 text-sm text-caption">{message}</p>
    </div>
  )
}
